/* eslint import/no-unresolved: 0 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";

/* ===== APIベース ===== */
// ENV にベースURLがあれば使う（例: http://localhost:4000）
// なければブラウザからは相対で /api/public を叩く
const RAW_API_BASE: string =
  (process.env.REACT_APP_API_BASE_URL as string) ||
  ((import.meta as any)?.env?.VITE_API_BASE_URL as string) ||
  "";

const API_PUBLIC = RAW_API_BASE
  ? `${RAW_API_BASE.replace(/\/+$/, "")}/api/public`
  : "/api/public";

const PAGE_SIZE = 10;

type ReviewItem = {
  id: string | number;
  planName?: string;
  createdAt?: string;
  userName?: string;
  gender?: string;
  rating?: number;
  recommend?: number;
  comment?: string;
  status?: "公開" | "非公開" | string;
};

export default function Reviews() {
  const [rows, setRows] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // UI 状態
  const [filter, setFilter] = useState<"all" | "high" | "latest">("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const res = await fetch(`${API_PUBLIC}/reviews`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (abort) return;

        const list = Array.isArray(data) ? data : data?.items ?? [];
        const normalized: ReviewItem[] = (list as any[]).map((x, i) => ({
          id: x.id ?? i + 1,
          planName: x.planName ?? x.plan ?? "",
          createdAt: x.createdAt ?? x.date ?? x.postedAt ?? "",
          // 投稿者は「ユーザー名」を優先して取る
          userName:
            x.userName ??
            x.username ??
            x.nickName ??
            x.nickname ??
            x.author ??
            "",
          gender: x.gender ?? "",
          rating: Number(x.rating ?? 0),
          recommend: Number(x.recommend ?? x.rating ?? 0),
          comment: x.comment ?? "",
          status: x.status ?? "公開",
        }));

        // 新着順で保持
        normalized.sort((a, b) => {
          const ta = new Date(a.createdAt || 0).getTime();
          const tb = new Date(b.createdAt || 0).getTime();
          return tb - ta;
        });

        setRows(normalized);
      } catch (e: any) {
        setErr(e?.message || "読み込みに失敗しました");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  // フィルタ後の全行
  const filteredRows = useMemo(() => {
    let r = rows;
    if (filter === "high") r = r.filter((x) => (x.rating ?? 0) >= 4);
    // latest は既に並び替え済みなのでそのまま
    return r;
  }, [rows, filter]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / PAGE_SIZE || 1)
  );
  const currentPage = Math.min(page, pageCount);

  // 現在ページに表示する 10件
  const viewRows = useMemo(() => {
    if (!filteredRows.length) return [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  // フィルタ変更時は1ページ目に戻す
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // 共通レイアウト（中央 1 列）
  const maxWidthStyle: React.CSSProperties = useMemo(
    () => ({
      maxWidth: 720,
      width: "100%",
      margin: "0 auto",
      padding: "16px 0 64px",
    }),
    []
  );

  // 白パネル
  const panel: React.CSSProperties = {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
    padding: 12,
  };

  const faded: React.CSSProperties = { opacity: 0.6 };

  // 各レビュー行（背景も白で固定）
  const rowWrap: React.CSSProperties = {
    padding: "12px 8px",
    borderBottom: "1px solid #eee",
    background: "#fff",
  };
  const lastRow: React.CSSProperties = {
    padding: "12px 8px",
    background: "#fff",
  };

  const btnBase: React.CSSProperties = {
    display: "inline-block",
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid #ddd",
    fontSize: 13,
    lineHeight: 1,
    cursor: "pointer",
    userSelect: "none",
    background: "#fff",
  };
  const btnActive: React.CSSProperties = {
    ...btnBase,
    borderColor: "#111",
    fontWeight: 700,
  };

  const fmtDate = (s?: string) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const hh = `${d.getHours()}`.padStart(2, "0");
    const mm = `${d.getMinutes()}`.padStart(2, "0");
    return `${y}-${m}-${day} ${hh}:${mm}`;
  };

  // ★を JSX で（色：ゴールド）
  const renderStars = (n = 0) => {
    const num = Math.max(0, Math.min(5, n));
    return (
      <span>
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            style={{
              color: i < num ? "#ffd700" : "#d1d5db",
              fontSize: 18,
              marginRight: 2,
            }}
          >
            ★
          </span>
        ))}
      </span>
    );
  };

  return (
    <>
      <Header />

      {/* 背景はグローバルそのまま、中央に白パネル */}
      <main className="bgScope lp" style={{ background: "transparent" }}>
        <div className="stack" style={maxWidthStyle}>
          {/* タイトル行 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
              レビュー一覧
            </h1>
            <Link to="/lp" style={{ textDecoration: "none", fontSize: 13 }}>
              ← トップへ戻る
            </Link>
          </div>

          {/* 中身パネル（背景：白） */}
          <section style={panel}>
            {/* 操作バー */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                padding: "4px 6px 10px",
              }}
            >
              <span style={{ fontSize: 12, ...faded }}>並び/絞り込み:</span>
              <button
                type="button"
                onClick={() => setFilter("latest")}
                style={filter === "latest" ? btnActive : btnBase}
              >
                新着順
              </button>
              <button
                type="button"
                onClick={() => setFilter("all")}
                style={filter === "all" ? btnActive : btnBase}
              >
                すべて
              </button>
              <button
                type="button"
                onClick={() => setFilter("high")}
                style={filter === "high" ? btnActive : btnBase}
              >
                ★4以上
              </button>
            </div>

            {/* 本文 */}
            {loading && <p style={{ padding: "12px 8px" }}>読み込み中…</p>}
            {err && (
              <p
                style={{
                  color: "crimson",
                  padding: "12px 8px",
                }}
              >
                {err}
              </p>
            )}

            {!loading && !err && rows.length === 0 && (
              <p style={{ padding: "12px 8px", ...faded }}>
                まだ公開レビューがありません。
              </p>
            )}

            {!loading &&
              !err &&
              viewRows.map((r, i) => (
                <article
                  key={r.id}
                  style={i === viewRows.length - 1 ? lastRow : rowWrap}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                        }}
                      >
                        {r.planName || "購入プラン不明"}
                      </div>
                      <div style={{ fontSize: 12, ...faded }}>
                        {fmtDate(r.createdAt)}
                      </div>
                    </div>

                    {/* 星（ゴールド） */}
                    <div>{renderStars(r.rating)}</div>
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    {r.comment || <span style={faded}>コメントなし</span>}
                  </div>

                  {/* 投稿者：ユーザー名のみ表示 */}
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      ...faded,
                    }}
                  >
                    投稿者: {r.userName || "会員様"}
                  </div>
                </article>
              ))}

            {/* ページネーション（10件ごとに前へ / 次へ） */}
            {!loading && !err && filteredRows.length > PAGE_SIZE && (
              <div
                style={{
                  textAlign: "center",
                  padding: "8px 0 4px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.max(1, p - 1))
                  }
                  disabled={currentPage <= 1}
                  style={{
                    ...btnBase,
                    opacity: currentPage <= 1 ? 0.5 : 1,
                  }}
                >
                  前へ
                </button>
                <span style={{ fontSize: 12, ...faded }}>
                  {currentPage} / {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(pageCount, p + 1))
                  }
                  disabled={currentPage >= pageCount}
                  style={{
                    ...btnBase,
                    opacity: currentPage >= pageCount ? 0.5 : 1,
                  }}
                >
                  次へ
                </button>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
